import debug from "./debugUtils";
import { programCodeFromSticker } from "./programLogoUtils";

const isAvailable = (buyingOption: AapiBuyingOption) => {
    // type maybe be IN_STOCK, PREORDER, or others (including empty) when available
    return buyingOption?.availability?.entity?.type !== "OUT_OF_STOCK";
};

export const isRpl = (kindleProgramLowCost?: AapiKindleProgramLowCost): boolean => {
    return programCodeFromSticker(kindleProgramLowCost?.stickerStyleCode) === "READ_PLUS_LISTEN";
};

export const hasKuUpsell = (buyingOptions?: [AapiBuyingOption]): boolean => {
    return !!buyingOptions?.find(buyingOption => buyingOption.type === "KINDLE_UNLIMITED_UPSELL" && isAvailable(buyingOption));
};

export const getKuUpsell = (buyingOptions?: [AapiBuyingOption]): AapiBuyingOption | undefined => {
    return buyingOptions?.find(buyingOption => buyingOption.type === "KINDLE_UNLIMITED_UPSELL" && isAvailable(buyingOption));
};

export const hasReadNowCta = (buyingOptions?: [AapiBuyingOption]): boolean => {
    return !!getBuyingOptionWithReadNowCta(buyingOptions);
};

export const getBuyingOptionWithReadNowCta = (buyingOptions?: [AapiBuyingOption]): AapiBuyingOption | undefined => {
    return buyingOptions?.find(buyingOption => !!buyingOption.callToAction?.entity?.readNow?.url && isAvailable(buyingOption));
};

export const getBorrowBuyingOption = (buyingOptions?: [AapiBuyingOption]): AapiBuyingOption | undefined => {
    return buyingOptions?.find(buyingOption => !!buyingOption.callToAction?.entity?.borrow?.url && isAvailable(buyingOption));
};

export const getKuBuyingOption = (buyingOptions?: [AapiBuyingOption]): AapiBuyingOption | undefined => {
    return buyingOptions?.find(buyingOption => !!(buyingOption.type === "KINDLE_UNLIMITED") && isAvailable(buyingOption));
};

export const hasBorrowCta = (buyingOptions?: [AapiBuyingOption]): boolean => {
    return !!getBorrowBuyingOption(buyingOptions);
};

export const hasPurchaseCta = (buyingOptions?: [AapiBuyingOption]): boolean => {
    return !!buyingOptions?.find(buyingOption => buyingOption.callToAction?.entity?.oneClick?.url && isAvailable(buyingOption));
};

export const hasAskConsentCta = (buyingOptions?: [AapiBuyingOption]): boolean => {
    if (debug.get("forceAskConsentAapiResponse")) {
        return true;
    }
    return !!buyingOptions?.find(buyingOption => buyingOption.callToAction?.entity?.askConsent?.url && isAvailable(buyingOption));
};

export const hasReKindleEligibleCta = (buyingOptions?: [AapiBuyingOption]): boolean => {
    return !!buyingOptions?.find(buyingOption => buyingOption.type === "KINDLE_ALC" && isAvailable(buyingOption));
};

export const hasPreOrderCta = (buyingOptions?: [AapiBuyingOption]): boolean => {
    return !!buyingOptions?.find(buyingOption => !!buyingOption.callToAction?.entity?.oneClickPreorder?.url && isAvailable(buyingOption));
};

export const hasLimberCta = (buyingOptions?: [AapiBuyingOption]): boolean => {
    return !!buyingOptions?.find(buyingOption => buyingOption.type === "LIMBER" && isAvailable(buyingOption));
};

export const hasSampleCta = (buyingOptions?: [AapiBuyingOption]): boolean => {
    return !!buyingOptions?.find(buyingOption => !!buyingOption.ebookSample?.entity?.endpoint?.url && isAvailable(buyingOption));
};

export const getByLine = (contributors?: [AapiContributor]): string | undefined => {
    return contributors?.filter(contributor => contributor.roles?.type === "author").map(author => author.name).join(", ")
        || contributors?.map(author => author.name).join(", ");
};

export const getAuthors = (contributors?: [AapiContributor]): Author[] | undefined => {
    return contributors?.map(author => {
        const authorPageUrl = author.links?.find(link => link.rel === "authorPageUrl" && link.url)?.url;
        const authorSearchPageUrl = author.links?.find(link => link.rel === "searchPageUrlFromConfig" && link.url)?.url;
        return {
            authorName: author.name,
            url: authorPageUrl ?? authorSearchPageUrl
        }
    });
};

export const asLegacyOverview = (item?: AapiAsinMetadata) => {
    return item?.productOverview?.attributes?.map(attribute => {
        const it: OverviewMetadata = {
            labelId: attribute.label?.id,
            labelContent: attribute.label?.displayContent,
            value: attribute.granularizedValue?.displayContent,
        };
        return it;
    });
};

export const primaryPurchaseCta = (buyingOptions?: [AapiBuyingOption]): AapiBuyingOption | undefined => {
    const oneClickCta = buyingOptions?.find(buyingOption => !!buyingOption.callToAction?.entity?.oneClick?.url && isAvailable(buyingOption));
    const oneClickPreorderCta = buyingOptions?.find(buyingOption => !!buyingOption.callToAction?.entity?.oneClickPreorder?.url && isAvailable(buyingOption));
    const askConsentCta = buyingOptions?.find(buyingOption => !!buyingOption.callToAction?.entity?.askConsent?.url && isAvailable(buyingOption));
    if (debug.get("forceAskConsentAapiResponse")) {
        // https://code.amazon.com/packages/AmazonAPIContracts/blobs/2ec0623189c8d2762b5362a75f45b5dd6c6a5baf/--/fixtures/products/offers/product.call-to-action.v1.json#L163
        const buyingOption : AapiBuyingOption = {
            callToAction: {
                entity: {
                    askConsent: {
                        "url": "/dp/EXAMPLE_ASIN_123",
                        "data": {
                            "displayString": "Ask for this",
                        }
                    }
                }
            }
        };
        return buyingOption;
    }
    return oneClickCta ?? oneClickPreorderCta ?? askConsentCta;
};

export const asLegacySeries = (item?: AapiAsinMetadata): SeriesMetadata | undefined => {
    const series = item?.bookSeries?.singleBookView?.series;
    if (series) {
        return {
            message: series.message?.longMessage || series.message?.shortMessage,
            url: series.link?.url,
        };
    }
};

export const canBuyForFree = (buyingOptions?: [AapiBuyingOption]): boolean => {
    return buyingOptions?.find(buyingOption => buyingOption.callToAction?.entity?.oneClick?.url && isAvailable(buyingOption) && buyingOption.type !== "LIMBER")?.price?.entity?.priceToPay?.moneyValueOrRange?.value?.amount === 0;
};

export const hasFreeAfrOption = (buyingOptions?: [AapiBuyingOption]): boolean => {
    const afrOption = buyingOptions?.find(buyingOption => buyingOption.callToAction?.entity?.oneClick?.url && isAvailable(buyingOption) && buyingOption.type === "LIMBER");
    return afrOption?.price?.entity?.priceToPay?.moneyValueOrRange?.value?.amount === 0
        || Number(afrOption?.callToAction?.entity?.oneClick?.data?.parameters?.find(param => param.name === "botmBuyButtonDisplayedPrice")?.value) === 0;
};

export const themedImages = (item?: AapiAsinMetadata): ThemedImages | undefined => {
    if (item?.kindleProgramLowCost?.themedImages) {
        return item.kindleProgramLowCost.themedImages;
    }
    const afrBuyingOption = item?.buyingOptions?.find(buyingOption => buyingOption.type === "LIMBER");
    if (afrBuyingOption?.kindleProgram?.entity?.themedImages) {
        return afrBuyingOption?.kindleProgram?.entity?.themedImages;
    }
};

export const isHearted = (item?: AapiAsinMetadata): boolean => {
    const hearted = item?.listsActions?.available?.find(action => action.actionType === "UN_HEART" || action.actionType === "SELECT_TO_UNHEART");
    if (hearted) {
        return true;
    }
    return false;
};


